import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { ApiError, api } from '@/lib/api'

/**
 * Tarefas: o quadro interno onde as terapeutas abrem cartões para o Luiz
 * desenvolver. Nada clínico mora aqui; é texto de produto, em claro.
 *
 * Os schemas seguem o contrato de `/api/tarefas`. Quem redesenha o quadro é o
 * canal ao vivo (`live.ts`): o evento chega e a query volta a buscar.
 */

const instante = z.string()
const instanteOuNulo = instante.nullable()

export const LABEL_COLOR_KEYS = [
  'green',
  'yellow',
  'orange',
  'red',
  'purple',
  'blue',
  'sky',
  'lime',
  'pink',
  'gray',
] as const

export const memberSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  image: z.string().nullable(),
})
export type Member = z.infer<typeof memberSchema>

export const boardSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  archived: z.boolean(),
  position: z.number(),
  createdByUserId: z.string().nullable(),
  createdAt: instante,
  listCount: z.number(),
  cardCount: z.number(),
})
export type BoardSummary = z.infer<typeof boardSummarySchema>

export const listSchema = z.object({
  id: z.string(),
  boardId: z.string(),
  name: z.string(),
  position: z.number(),
  archived: z.boolean(),
  color: z.string().nullable(),
})
export type KanbanList = z.infer<typeof listSchema>

export const labelSchema = z.object({
  id: z.string(),
  boardId: z.string(),
  name: z.string(),
  color: z.string(),
})
export type Label = z.infer<typeof labelSchema>

export const cardFaceSchema = z.object({
  id: z.string(),
  boardId: z.string(),
  listId: z.string(),
  title: z.string(),
  position: z.number(),
  dueAt: instanteOuNulo,
  archived: z.boolean(),
  hasDescription: z.boolean(),
  labelIds: z.array(z.string()),
  assigneeIds: z.array(z.string()),
  checklistDone: z.number(),
  checklistTotal: z.number(),
  commentCount: z.number(),
  attachmentCount: z.number(),
  createdAt: instante,
  updatedAt: instante,
  createdByUserId: z.string().nullable(),
  movedAt: instanteOuNulo,
  version: z.number(),
})
export type CardFace = z.infer<typeof cardFaceSchema>

export const boardDetailSchema = boardSummarySchema.extend({
  lists: z.array(listSchema),
  cards: z.array(cardFaceSchema),
  labels: z.array(labelSchema),
})
export type BoardDetail = z.infer<typeof boardDetailSchema>

export const itemSchema = z.object({
  id: z.string(),
  cardId: z.string(),
  text: z.string(),
  done: z.boolean(),
  position: z.number(),
})
export type ChecklistItem = z.infer<typeof itemSchema>

export const commentSchema = z.object({
  id: z.string(),
  cardId: z.string(),
  authorUserId: z.string().nullable(),
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: instante,
  editedAt: instanteOuNulo,
})
export type Comment = z.infer<typeof commentSchema>

export const attachmentSchema = z.object({
  id: z.string(),
  cardId: z.string(),
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  uploadedByUserId: z.string().nullable(),
  createdAt: instante,
})
export type Attachment = z.infer<typeof attachmentSchema>

export const activitySchema = z.object({
  id: z.string(),
  action: z.string(),
  actorUserId: z.string().nullable(),
  actorName: z.string().nullable(),
  oldValue: z.string().nullable(),
  newValue: z.string().nullable(),
  createdAt: instante,
})
export type Activity = z.infer<typeof activitySchema>

export const cardDetailSchema = cardFaceSchema.extend({
  description: z.string().nullable(),
  agentPrompt: z.string().nullable(),
  checklist: z.array(itemSchema),
  comments: z.array(commentSchema),
  attachments: z.array(attachmentSchema),
  activity: z.array(activitySchema),
})
export type CardDetail = z.infer<typeof cardDetailSchema>

export const mentionSchema = z.object({
  id: z.string(),
  kind: z.enum(['mention', 'deployed']),
  boardId: z.string(),
  cardId: z.string(),
  commentId: z.string().nullable(),
  cardTitle: z.string(),
  authorUserId: z.string().nullable(),
  authorName: z.string().nullable(),
  excerpt: z.string().nullable(),
  createdAt: instante,
  readAt: instanteOuNulo,
})
export type Mention = z.infer<typeof mentionSchema>

export const voiceTaskSchema = z.object({
  create: z.boolean(),
  kind: z.string(),
  title: z.string(),
  descriptionMd: z.string(),
  boardId: z.string().nullable(),
  listId: z.string().nullable(),
  labelNames: z.array(z.string()),
  assigneeIds: z.array(z.string()),
  checklist: z.array(z.string()),
  dueHint: z.string().nullable(),
  agentPrompt: z.string().nullable(),
  mergeIntoCardId: z.string().nullable(),
})
export type VoiceTask = z.infer<typeof voiceTaskSchema>

export const VOICE_STATUSES = [
  'transcribing',
  'planning',
  'review',
  'applying',
  'done',
  'failed',
  'discarded',
] as const
export type VoiceStatus = (typeof VOICE_STATUSES)[number]

export const voiceNoteSchema = z.object({
  id: z.string(),
  createdByUserId: z.string().nullable(),
  status: z.enum(VOICE_STATUSES),
  promptId: z.string().nullable(),
  audioSeconds: z.number().nullable(),
  audioBytes: z.number(),
  transcript: z.string().nullable(),
  plan: z
    .object({ kind: z.string(), tasks: z.array(voiceTaskSchema), notes: z.string() })
    .nullable(),
  result: z
    .object({
      cards: z.array(
        z.object({
          cardId: z.string(),
          boardId: z.string(),
          title: z.string(),
          merged: z.boolean(),
        }),
      ),
    })
    .nullable(),
  error: z.string().nullable(),
  modelTranscribe: z.string().nullable(),
  modelPlan: z.string().nullable(),
  tokensIn: z.number().nullable(),
  tokensOut: z.number().nullable(),
  createdAt: instante,
  finishedAt: instanteOuNulo,
})
export type VoiceNote = z.infer<typeof voiceNoteSchema>

/* ---- chaves --------------------------------------------------------------- */

export const TAREFAS_QK = ['tarefas'] as const
export const BOARDS_QK = ['tarefas', 'boards'] as const
export const boardQk = (boardId: string) => ['tarefas', 'board', boardId] as const
export const cardQk = (cardId: string) => ['tarefas', 'card', cardId] as const
export const MEMBERS_QK = ['tarefas', 'members'] as const
export const MENTIONS_QK = ['tarefas', 'mentions'] as const
export const VOICE_QK = ['tarefas', 'voice'] as const
export const voiceQk = (id: string) => ['tarefas', 'voice', id] as const
export const CONFIG_QK = ['tarefas', 'config'] as const

const B = '/api/tarefas'

/* ---- leitura -------------------------------------------------------------- */

export function useTarefasConfig(): UseQueryResult<{ voice: boolean; voiceReason: string | null }> {
  return useQuery({
    queryKey: CONFIG_QK,
    queryFn: ({ signal }) =>
      api(`${B}/config`, {
        schema: z.object({ voice: z.boolean(), voiceReason: z.string().nullable() }),
        signal,
      }),
    staleTime: 5 * 60_000,
  })
}

export function useMembers(): UseQueryResult<Member[]> {
  return useQuery({
    queryKey: MEMBERS_QK,
    queryFn: async ({ signal }) =>
      (await api(`${B}/members`, { schema: z.object({ items: z.array(memberSchema) }), signal }))
        .items,
    staleTime: 5 * 60_000,
  })
}

export function useBoards(archived: boolean): UseQueryResult<BoardSummary[]> {
  return useQuery({
    queryKey: [...BOARDS_QK, archived],
    queryFn: async ({ signal }) =>
      (
        await api(`${B}/boards${archived ? '?archived=1' : ''}`, {
          schema: z.object({ items: z.array(boardSummarySchema) }),
          signal,
        })
      ).items,
  })
}

export function fetchBoard(boardId: string, signal?: AbortSignal): Promise<BoardDetail> {
  return api(`${B}/boards/${boardId}`, {
    schema: boardDetailSchema,
    ...(signal === undefined ? {} : { signal }),
  })
}

/** O foco na aba fica como rede: se o canal caiu com a aba escondida, voltar busca. */
export function useBoard(boardId: string): UseQueryResult<BoardDetail> {
  return useQuery({
    queryKey: boardQk(boardId),
    queryFn: ({ signal }) => fetchBoard(boardId, signal),
    enabled: boardId !== '',
    refetchOnWindowFocus: true,
  })
}

export function useCard(cardId: string): UseQueryResult<CardDetail> {
  return useQuery({
    queryKey: cardQk(cardId),
    queryFn: ({ signal }) => api(`${B}/cards/${cardId}`, { schema: cardDetailSchema, signal }),
  })
}

export function useMentions(): UseQueryResult<{ items: Mention[]; unread: number }> {
  return useQuery({
    queryKey: MENTIONS_QK,
    queryFn: ({ signal }) =>
      api(`${B}/mentions?limit=20`, {
        schema: z.object({ items: z.array(mentionSchema), unread: z.number() }),
        signal,
      }),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })
}

export function useVoiceNotes(): UseQueryResult<VoiceNote[]> {
  return useQuery({
    queryKey: [...VOICE_QK, 'list'],
    queryFn: async ({ signal }) =>
      (
        await api(`${B}/voice?limit=20`, {
          schema: z.object({ items: z.array(voiceNoteSchema) }),
          signal,
        })
      ).items,
  })
}

export function useVoiceNote(id: string | null, enabled: boolean): UseQueryResult<VoiceNote> {
  return useQuery({
    queryKey: voiceQk(id ?? ''),
    queryFn: ({ signal }) => api(`${B}/voice/${id}`, { schema: voiceNoteSchema, signal }),
    enabled: enabled && id !== null,
  })
}

/* ---- escrita -------------------------------------------------------------- */

export const tarefasApi = {
  createBoard: (body: { name: string; description: string | null }) =>
    api(`${B}/boards`, { method: 'POST', body, schema: boardSummarySchema }),
  patchBoard: (
    boardId: string,
    body: { name?: string; description?: string | null; archived?: boolean; position?: number },
  ) => api(`${B}/boards/${boardId}`, { method: 'PATCH', body, schema: boardDetailSchema }),
  deleteBoard: (boardId: string) => api(`${B}/boards/${boardId}`, { method: 'DELETE' }),

  createList: (boardId: string, name: string) =>
    api(`${B}/boards/${boardId}/lists`, { method: 'POST', body: { name }, schema: listSchema }),
  patchList: (
    listId: string,
    body: { name?: string; archived?: boolean; position?: number; color?: string | null },
  ) => api(`${B}/lists/${listId}`, { method: 'PATCH', body, schema: listSchema }),
  deleteList: (listId: string) => api(`${B}/lists/${listId}`, { method: 'DELETE' }),

  createCard: (
    listId: string,
    body: { title: string; description: string | null; dueAt: string | null; atTop: boolean },
  ) => api(`${B}/lists/${listId}/cards`, { method: 'POST', body, schema: cardFaceSchema }),
  patchCard: (cardId: string, body: CardPatch) =>
    api(`${B}/cards/${cardId}`, { method: 'PATCH', body, schema: cardDetailSchema }),
  moveCard: (
    cardId: string,
    body: { listId: string; beforeId?: string | null; afterId?: string | null; position?: number },
  ) => api(`${B}/cards/${cardId}/move`, { method: 'POST', body, schema: cardFaceSchema }),
  deleteCard: (cardId: string) => api(`${B}/cards/${cardId}`, { method: 'DELETE' }),

  createLabel: (boardId: string, body: { name: string; color: string }) =>
    api(`${B}/boards/${boardId}/labels`, { method: 'POST', body, schema: labelSchema }),
  patchLabel: (labelId: string, body: { name?: string; color?: string }) =>
    api(`${B}/labels/${labelId}`, { method: 'PATCH', body, schema: labelSchema }),
  deleteLabel: (labelId: string) => api(`${B}/labels/${labelId}`, { method: 'DELETE' }),

  addItem: (cardId: string, text: string) =>
    api(`${B}/cards/${cardId}/checklist`, { method: 'POST', body: { text }, schema: itemSchema }),
  patchItem: (itemId: string, body: { text?: string; done?: boolean; position?: number }) =>
    api(`${B}/checklist/${itemId}`, { method: 'PATCH', body, schema: itemSchema }),
  deleteItem: (itemId: string) => api(`${B}/checklist/${itemId}`, { method: 'DELETE' }),

  addComment: (cardId: string, body: string) =>
    api(`${B}/cards/${cardId}/comments`, {
      method: 'POST',
      body: { body },
      schema: commentSchema,
    }),
  patchComment: (commentId: string, body: string) =>
    api(`${B}/comments/${commentId}`, { method: 'PATCH', body: { body }, schema: commentSchema }),
  deleteComment: (commentId: string) => api(`${B}/comments/${commentId}`, { method: 'DELETE' }),

  readMention: (mentionId: string) => api(`${B}/mentions/${mentionId}/read`, { method: 'POST' }),
  readAllMentions: () => api(`${B}/mentions/read`, { method: 'POST' }),

  deleteAttachment: (attachmentId: string) =>
    api(`${B}/attachments/${attachmentId}`, { method: 'DELETE' }),

  applyVoice: (noteId: string, tasks: VoiceTask[]) =>
    api(`${B}/voice/${noteId}/apply`, {
      method: 'POST',
      body: { tasks },
      schema: voiceNoteSchema,
    }),
  discardVoice: (noteId: string) =>
    api(`${B}/voice/${noteId}/discard`, { method: 'POST', schema: voiceNoteSchema }),
  retryVoice: (noteId: string) =>
    api(`${B}/voice/${noteId}/retry`, { method: 'POST', schema: voiceNoteSchema }),
}

/** Campo ausente é "não mexer"; `dueAt: null` é "tirar o prazo". */
export interface CardPatch {
  title?: string
  description?: string | null
  agentPrompt?: string | null
  dueAt?: string | null
  archived?: boolean
  labelIds?: string[]
  assigneeIds?: string[]
  version?: number
}

export const attachmentUrl = (attachmentId: string) => `${B}/attachments/${attachmentId}`

/**
 * Corpo cru em vez de multipart: a API não tem parser de formulário e o
 * arquivo é um só. O nome viaja no cabeçalho, codificado, porque cabeçalho
 * HTTP não leva acento.
 */
async function postRaw<T>(
  path: string,
  body: Blob,
  headers: Record<string, string>,
  schema: z.ZodType<T>,
): Promise<T> {
  let response: Response
  try {
    response = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': body.type || 'application/octet-stream', ...headers },
      body,
    })
  } catch {
    throw new ApiError(0, 'servidor-fora', 'Não foi possível falar com o servidor.')
  }
  const payload: unknown = await response.json().catch(() => ({}))
  if (!response.ok) {
    const p = (payload ?? {}) as Record<string, unknown>
    throw new ApiError(
      response.status,
      typeof p.code === 'string' ? p.code : 'erro',
      typeof p.message === 'string' ? p.message : 'Não deu para enviar o arquivo.',
    )
  }
  const parsed = schema.safeParse(payload)
  if (!parsed.success) {
    throw new ApiError(response.status, 'resposta-invalida', 'O servidor respondeu fora do padrão.')
  }
  return parsed.data
}

export function uploadAttachment(cardId: string, file: File): Promise<Attachment> {
  return postRaw(
    `${B}/cards/${cardId}/attachments`,
    file,
    { 'x-filename': encodeURIComponent(file.name || 'arquivo') },
    attachmentSchema,
  )
}

export function startVoice(blob: Blob, seconds: number): Promise<VoiceNote> {
  return postRaw(`${B}/voice?seconds=${seconds.toFixed(1)}`, blob, {}, voiceNoteSchema)
}

/** O 409 de cartão defasado traz o cartão de agora em `details.card`. */
export function staleCard(error: unknown): CardDetail | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  const parsed = cardDetailSchema.safeParse(error.details.card)
  return parsed.success ? parsed.data : null
}

export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}
